import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import './TeamManagement.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_PERMISSIONS = {
  owner: {
    label: 'Owner',
    color: '#8B5CF6',
    permissions: ['Full access', 'Manage users', 'Delete brand']
  },
  admin: {
    label: 'Admin',
    color: '#3B82F6',
    permissions: ['Manage products', 'Manage users', 'View analytics']
  },
  editor: {
    label: 'Editor',
    color: '#10B981',
    permissions: ['Create products', 'Edit products', 'View analytics']
  },
  viewer: {
    label: 'Viewer',
    color: '#6B7280',
    permissions: ['View products', 'View analytics']
  }
};

const TeamManagement = () => {
  const navigate = useNavigate();
  const { brandId } = useParams();
  const { sessionToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState(null);
  const [users, setUsers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, [brandId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load brand details
      const brandRes = await axios.get(
        `${API}/brands/${brandId}`,
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );

      if (brandRes.data.success) {
        setBrand(brandRes.data.brand);
      }

      // Load team members
      const usersRes = await axios.get(
        `${API}/brands/${brandId}/users`,
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );

      if (usersRes.data.success) {
        setUsers(usersRes.data.users);
      }
    } catch (error) {
      console.error('Error loading team data:', error);
      setError(error.response?.data?.detail || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      const response = await axios.post(
        `${API}/brands/${brandId}/users/invite`,
        {
          user_email: inviteEmail,
          role: inviteRole
        },
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );

      if (response.data.success) {
        setSuccess(`User invited successfully as ${inviteRole}`);
        setInviteEmail('');
        setInviteRole('viewer');
        setShowInviteModal(false);
        loadData();
      }
    } catch (error) {
      console.error('Error inviting user:', error);
      setError(error.response?.data?.detail || 'Failed to invite user');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      setError('');
      setSuccess('');

      const response = await axios.put(
        `${API}/brands/${brandId}/users/${userId}`,
        { role: newRole },
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );

      if (response.data.success) {
        setSuccess('User role updated successfully');
        loadData();
      }
    } catch (error) {
      console.error('Error updating role:', error);
      setError(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user from the team?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');

      const response = await axios.delete(
        `${API}/brands/${brandId}/users/${userId}`,
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );

      if (response.data.success) {
        setSuccess('User removed successfully');
        loadData();
      }
    } catch (error) {
      console.error('Error removing user:', error);
      setError(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="team-management">
          <div className="loading">Loading team data...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="team-management">
        <div className="team-header">
          <div>
            <button className="back-button" onClick={() => navigate('/merchant/brands')}>
              ← Back to Brands
            </button>
            <h1>👥 Team Management</h1>
            <p className="brand-name">{brand?.name}</p>
          </div>
          <button className="btn-primary" onClick={() => setShowInviteModal(true)}>
            + Invite Team Member
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="team-list">
          <div className="list-header">
            <h2>Team Members ({users.length})</h2>
          </div>

          <div className="users-table">
            {users.map(user => (
              <div key={user.id} className="user-row">
                <div className="user-info">
                  <div className="user-avatar">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="user-details">
                    <h3>{user.name}</h3>
                    <p>{user.email}</p>
                    {user.invited_at && (
                      <span className="invited-date">
                        Joined: {new Date(user.invited_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="user-role">
                  <span 
                    className="role-badge"
                    style={{ 
                      backgroundColor: `${ROLE_PERMISSIONS[user.role]?.color}20`,
                      color: ROLE_PERMISSIONS[user.role]?.color
                    }}
                  >
                    {ROLE_PERMISSIONS[user.role]?.label || user.role}
                  </span>
                  
                  {!user.is_owner && (
                    <div className="user-actions">
                      <select
                        value={user.role}
                        onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                        className="role-select"
                      >
                        <option value="admin">Admin</option>
                        <option value="editor">Editor</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      <button
                        className="btn-remove"
                        onClick={() => handleRemoveUser(user.id)}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="permissions-guide">
          <h3>Role Permissions Guide</h3>
          <div className="roles-grid">
            {Object.entries(ROLE_PERMISSIONS).map(([key, role]) => (
              <div key={key} className="role-card">
                <div 
                  className="role-header"
                  style={{ backgroundColor: `${role.color}20`, color: role.color }}
                >
                  <h4>{role.label}</h4>
                </div>
                <ul className="permissions-list">
                  {role.permissions.map((perm, idx) => (
                    <li key={idx}>✓ {perm}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {showInviteModal && (
          <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Invite Team Member</h2>
              <p className="modal-subtitle">
                Add a user to manage this brand
              </p>

              <form onSubmit={handleInviteUser}>
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    placeholder="user@example.com"
                  />
                  <p className="form-help">
                    User must have an existing FITTTY account
                  </p>
                </div>

                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                  >
                    <option value="admin">Admin - Full management access</option>
                    <option value="editor">Editor - Can create and edit products</option>
                    <option value="viewer">Viewer - Read-only access</option>
                  </select>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowInviteModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Send Invitation
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TeamManagement;
